import React, { useEffect } from "react";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";

// 🌟 Configuration for each Modal Type
const modalConfigs = {
  center: {
    wrapper: "justify-center items-center p-4 sm:p-6",
    box: "w-full max-w-5xl h-auto max-h-[90vh] rounded-3xl",
    initial: { scale: 0.95, y: 20 },
    animate: { scale: 1, y: 0 },
    exit: { scale: 0.95, y: 20 },
  },
  fullscreen: {
    wrapper: "justify-center items-center",
    box: "w-full h-full rounded-none border-0",
    initial: { scale: 0.95, y: 20 },
    animate: { scale: 1, y: 0 },
    exit: { scale: 0.95, y: 20 },
  },
  right: {
    wrapper: "justify-end items-stretch",
    box: "w-full max-w-md h-full rounded-s-3xl border-y-0 border-e-0",
    initial: { x: "100%" },
    animate: { x: 0 },
    exit: { x: "100%" },
  },
  left: {
    wrapper: "justify-start items-stretch",
    box: "w-full max-w-md h-full rounded-e-3xl border-y-0 border-s-0",
    initial: { x: "-100%" },
    animate: { x: 0 },
    exit: { x: "-100%" },
  },
  top: {
    wrapper: "justify-center items-start p-4 sm:p-6",
    box: "w-full max-w-5xl h-auto max-h-[70vh] rounded-3xl",
    initial: { y: "-100%" },
    animate: { y: 0 },
    exit: { y: "-100%" },
  },
  bottom: {
    wrapper: "justify-center items-end sm:p-6 p-0", // Touches edges on mobile, padding on desktop
    box: "w-full max-w-5xl h-auto max-h-[85vh] rounded-t-3xl sm:rounded-3xl border-b-0",
    initial: { y: "100%" },
    animate: { y: 0 },
    exit: { y: "100%" },
  }
};

const Modal = ({ isOpen, onClose, children, modalType = "center" }) => {
  
  // 🌟 Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Fallback to center if an invalid type is passed
  const config = modalConfigs[modalType] || modalConfigs.center;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="modal-root"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`fixed inset-0 z-[1000] flex pointer-events-none ${config.wrapper}`}
          dir="rtl"
        >
          {/* 🌟 Blurry Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto"
            onClick={onClose}
          />

          {/* 🌟 Modal Content Box */}
          <motion.div
            layout // Magic! Makes changing modalType while open morph smoothly
            initial={config.initial}
            animate={config.animate}
            exit={config.exit}
            transition={{ type: "spring", bounce: 0.1, duration: 0.4 }}
            className={`bg-bg-primary border border-border shadow-2xl overflow-hidden flex flex-col pointer-events-auto relative z-10 ${config.box}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Floating Close Button */}
            <button
              onClick={onClose}
              className="absolute top-3 end-3 p-2 bg-bg-tertiary/80 backdrop-blur-md text-text-secondary hover:text-status-danger hover:bg-status-danger/10 rounded-xl transition-all shadow-sm z-[100]"
              title="إغلاق"
            >
              <Icon icon="solar:close-square-bold-duotone" className="text-2xl" />
            </button>

            {/* Scrollable Children Container */}
            <div className={`w-full h-full ${modalType === 'fullscreen'? "" : "overflow-y-auto"}  p-6 md:p-8 pt-14 md:pt-14 custom-scrollbar`}>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal;